import { supabase } from "@/integrations/supabase";
import { Logger } from "@/utils";
import { ROUTES_PATH } from "@/router";
import type {
  RealtimeChannel,
  RealtimePostgresChangesFilter,
  RealtimePostgresChangesPayload,
  Session,
} from "@supabase/supabase-js";
import type {
  Database,
  FunctionArgsRPC,
  FunctionNameRPC,
  FunctionReturnsRPC,
} from "./types";

/**
 * Singleton class that provides a wrapper around Supabase authentication and functions.
 * Handles user authentication flows including login, signup, password reset, and session management.
 * All authentication methods include error logging for debugging purposes.
 */
class SupabaseInstance {
  /**
   * Signs in a user to Supabase using their email and password.
   * @param email - The email address of the user to sign in.
   * @param password - The password of the user to sign in.
   * @returns A promise that resolves with an object containing the result of the sign in operation. If successful, `data` contains the auth response with user and session. If it fails, `error` contains the error details.
   */
  async login(email: string, password: string) {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { data, error };
    } catch (error) {
      Logger.loggerError({
        section: "login",
        message: error,
      });
      throw error;
    }
  }

  /**
   * Signs up a user to Supabase using their email and password.
   * Sends a confirmation email to the provided email address.
   * @param email - The email address of the user to sign up.
   * @param password - The password of the user to sign up.
   * @returns A promise that resolves with an object containing the result of the sign up operation. If successful, `data` contains the auth response. If it fails, `error` contains the error details.
   */
  async signUp(email: string, password: string) {
    try {
      const redirectUrl = `${window.location.origin}${ROUTES_PATH.LOGIN}`;
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
        },
      });
      return { data, error };
    } catch (error) {
      Logger.loggerError({
        section: "sign_up",
        message: error,
      });
      throw error;
    }
  }

  /**
   * Resets the password for the given email address.
   * Sends a password recovery email with a link to the update password page.
   * @param email - The email address to reset the password for.
   * @returns A promise that resolves with an object containing the result of the reset operation. If successful, `error` will be `null`. If it fails, `error` contains the error details.
   */
  async resetPasswordForEmail(email: string) {
    try {
      const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}${ROUTES_PATH.UPDATE_PASSWORD}`,
      });
      return { data, error };
    } catch (error) {
      Logger.loggerError({
        section: "reset_password",
        message: error,
      });
      throw error;
    }
  }

  /**
   * Resends the signup confirmation email to the given address.
   * No-op-safe: Supabase does not reveal whether the email exists.
   * Does NOT interpret the error — the caller distinguishes rate limit from real failure.
   * @param email - The email address to resend the confirmation to.
   * @returns A promise that resolves with `{ data, error }`. `error.code` is
   * `over_email_send_rate_limit` when the per-address send window is still open.
   */
  async resendConfirmation(email: string) {
    try {
      const redirectUrl = `${window.location.origin}${ROUTES_PATH.LOGIN}`;
      const { data, error } = await supabase.auth.resend({
        type: "signup",
        email,
        options: {
          emailRedirectTo: redirectUrl,
        },
      });
      return { data, error };
    } catch (error) {
      Logger.loggerError({
        section: "resend_confirmation",
        message: error,
      });
      throw error;
    }
  }

  /**
   * Updates the password for the current authenticated user.
   * Clears recovery session metadata after successful password update.
   * @param password - The new password for the user.
   * @returns A promise that resolves with an object containing the result of the update operation. If successful, `data` contains the updated user. If it fails, `error` contains the error details.
   */
  async updatePassword(password: string) {
    try {
      const { data, error } = await supabase.auth.updateUser({
        password,
        data: {
          is_recovery_session: false,
          recovery_timestamp: null,
        },
      });
      return { data, error };
    } catch (error) {
      Logger.loggerError({
        section: "update_password",
        message: error,
      });
      throw error;
    }
  }

  /**
   * Signs out the current user from Supabase.
   * Clears all session data and tokens.
   * @returns A promise that resolves when the user has been signed out successfully.
   * @throws Will throw an error if the sign out operation fails.
   */
  async logout() {
    try {
      return await supabase.auth.signOut();
    } catch (error) {
      Logger.loggerError({
        section: "logout",
        message: error,
      });
      throw error;
    }
  }

  /**
   * Gets the current session for the authenticated user.
   * Retrieves the active session from local storage without making a network request.
   * @returns A promise that resolves with an object containing the current session data.
   * @throws Will throw an error if the session retrieval operation fails.
   */
  async getSession() {
    try {
      return await supabase.auth.getSession();
    } catch (error) {
      Logger.loggerError({
        section: "get_session",
        message: error,
      });
      throw error;
    }
  }

  /**
   * Gets the current user from the server.
   * Unlike getSession(), this always makes a network request to get fresh user data.
   * Use this when you need up-to-date user metadata.
   * @returns A promise that resolves with an object containing the current user data.
   * @throws Will throw an error if the user retrieval operation fails.
   */
  async getUser() {
    try {
      return await supabase.auth.getUser();
    } catch (error) {
      Logger.loggerError({
        section: "get_user",
        message: error,
      });
      throw error;
    }
  }

  /**
   * Listens for authentication state changes (sign in, sign out, token refresh, etc.).
   * The callback will be invoked whenever the auth state changes.
   * @param callback - The callback function invoked when authentication state changes. Receives the event type and current session.
   * @returns An object with an `unsubscribe` method to stop listening for authentication state changes.
   */
  onAuthStateChange(
    callback: (event: string, session: Session | null) => void,
  ) {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(callback);
    return {
      unsubscribe: () => subscription.unsubscribe(),
    };
  }

  /**
   * Invokes a Supabase Edge Function with the given payload.
   * @template P - The type of the payload to be passed to the Edge Function.
   * @param functionName - The name of the Edge Function to invoke.
   * @param payload - The payload to be passed to the Edge Function.
   * @returns A promise that resolves with the response from the Edge Function invocation.
   * @throws Will throw an error if the Edge Function invocation fails.
   */
  invokeFunction<P>(functionName: string, payload: P) {
    try {
      return supabase.functions.invoke(functionName, {
        body: payload ?? {},
      });
    } catch (error) {
      Logger.loggerError({
        section: "invoke_function",
        message: error,
      });
      throw error;
    }
  }

  /**
   * Refreshes the current user session using a refresh token.
   * Used to obtain a new access token when the current one expires.
   * @param refresh_token - The refresh token to use for obtaining a new session.
   * @returns A promise that resolves with an object containing the refreshed session data.
   * @throws Will throw an error if the session refresh operation fails.
   */
  async refreshSession(refresh_token: string) {
    try {
      return await supabase.auth.refreshSession({ refresh_token });
    } catch (error) {
      Logger.loggerError({
        section: "refresh_session",
        message: error,
      });
      throw error;
    }
  }

  /**
   * Invokes a Supabase Remote Procedure Call.
   */
  async rpc<T extends FunctionNameRPC>(
    functionName: T,
    payload: FunctionArgsRPC<T>,
  ): Promise<FunctionReturnsRPC<T>> {
    try {
      const { data, error: rpcError } = await supabase.rpc(
        functionName,
        payload,
      );
      if (rpcError) throw rpcError;
      return data as unknown as FunctionReturnsRPC<T>;
    } catch (error) {
      Logger.loggerError({
        section: "rpc_function",
        message: error,
      });
      throw error;
    }
  }

  onChannel(
    channelName: string,
    filters: RealtimePostgresChangesFilter<
      "*" | "INSERT" | "UPDATE" | "DELETE"
    >,
    callback: (
      payload: RealtimePostgresChangesPayload<Record<string, unknown>>,
    ) => void,
  ): RealtimeChannel {
    // Cast needed: SDK defines separate overloads per event type ("*", "INSERT", etc.)
    // so a union filter type won't match any single overload at compile time.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (supabase.channel(channelName) as any)
      .on("postgres_changes", filters, callback)
      .subscribe();
  }

  getDataFrom<TableName extends string & keyof Database["public"]["Tables"]>(
    tableName: TableName,
  ) {
    return supabase.from(tableName);
  }
}

export default new SupabaseInstance();
